import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Container,
  Icon,
  useToast,
  Progress,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Badge,
  IconButton,
  Divider,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  InputGroup,
  InputLeftElement,
  Image,
} from '@chakra-ui/react';
import {
  FaStethoscope,
  FaUser,
  FaCloudUploadAlt,
  FaTimes,
  FaCheckCircle,
  FaPlus,
  FaTrash,
  FaClipboardList,
  FaIdCard,
  FaAddressCard,
  FaVial,
} from 'react-icons/fa';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, AddIcon } from '@chakra-ui/icons';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

// ── Helpers ──────────────────────────────────────────────────────────────────────

async function generateBatchDrawCodes(count) {
  try {
    const { data } = await supabase
      .from('submissions')
      .select('draw_code')
      .not('draw_code', 'is', null)
      .order('draw_code', { ascending: false })
      .limit(100);

    let max = 1000;
    if (data) {
      for (const row of data) {
        const num = parseInt(row.draw_code, 10);
        if (!isNaN(num) && num > max) max = num;
      }
    }
    return Array.from({ length: count }, (_, i) => String(max + 1 + i));
  } catch {
    const base = String(Date.now()).slice(-7);
    return Array.from({ length: count }, (_, i) => `${base}${i}`);
  }
}

async function uploadFileToStorage(file, type) {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const filePath = `${type}/${fileName}`;

  const { error } = await supabase.storage
    .from('uploads')
    .upload(filePath, file, { contentType: file.type || 'application/octet-stream', upsert: true });

  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('uploads')
    .getPublicUrl(filePath);

  return publicUrl;
}

const STEPS = [
  { label: 'Batch Info', icon: FaStethoscope },
  { label: 'Add Patients', icon: FaUser },
  { label: 'Review & Submit', icon: FaClipboardList },
];

// ── FileDropZone ─────────────────────────────────────────────────────────────────

function FileDropZone({ label, icon, color, files, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (fileList) => {
    const arr = Array.from(fileList).filter(
      (f) => f.type?.startsWith('image/') || f.type === 'application/pdf'
    );
    if (arr.length) onAdd(arr);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag(e.type === 'dragenter' || e.type === 'dragover');
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Box
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        p={3}
        borderWidth={2}
        borderStyle="dashed"
        borderRadius="md"
        bg={drag ? `${color}.50` : 'gray.50'}
        borderColor={drag ? `${color}.400` : 'gray.200'}
        cursor="pointer"
        textAlign="center"
        _hover={{ borderColor: `${color}.400` }}
        transition="all 0.2s"
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <Icon as={icon || FaCloudUploadAlt} color="gray.400" mr={2} />
        <Text as="span" fontSize="sm" color="gray.500">
          {files.length > 0 ? `${files.length} file(s)` : 'Drop or click'}
        </Text>
      </Box>
      {files.length > 0 && (
        <VStack align="stretch" mt={1} spacing={0}>
          {files.map((f, i) => (
            <HStack key={i} justify="space-between" fontSize="xs" px={1}>
              <Text isTruncated flex={1}>
                {f.name}
              </Text>
              <IconButton
                size="xs"
                icon={<FaTimes />}
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(i);
                }}
                aria-label="Remove"
              />
            </HStack>
          ))}
        </VStack>
      )}
    </FormControl>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────────

export default function BulkUpload() {
  const { id: phlebotomistId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Wizard step
  const [step, setStep] = useState(0);

  // Phlebotomist profile
  const [phleb, setPhleb] = useState(null);
  const [phlebLoading, setPhlebLoading] = useState(true);

  // Step 1: Doctor
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const doctorDropdownRef = useRef(null);

  // Step 1: Client name
  const [clientName, setClientName] = useState('');

  // Step 1: Lab
  const [labs, setLabs] = useState([]);
  const [labSearch, setLabSearch] = useState('');
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const labDropdownRef = useRef(null);

  // Step 1: Tests
  const [allTests, setAllTests] = useState([]);
  const [testSearch, setTestSearch] = useState('');
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);
  const testDropdownRef = useRef(null);

  // Insurance
  const [allInsurances, setAllInsurances] = useState([]);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [showInsuranceDropdown, setShowInsuranceDropdown] = useState(false);
  const insuranceDropdownRef = useRef(null);

  // Specimen types list (same as BloodDrawForm)
  const specimenTypes = [
    'Blood - General', 'BLOOD SMEAR', 'Blue Top tube', 'Ear Swab',
    'Endocervical swab', 'Eye Swab', 'Fecal Smear', 'Fecal Swab', 'Fluid',
    'Gray Top tube - Sodium Flouride', 'GREEN-TOP TUBE', 'HAIR', 'iFOBT Stool',
    'Lavender Top Tube', 'Lesion swab', 'Light Blue Tube', 'MICROGRAPH', 'MISC',
    'Nail Clippings', 'NAILS', 'Nasal (mid-turbinate) swab', 'Nasal Swab (Default)',
    'Nasopharyngeal swab', 'Oral Fluid Toxicology', 'Oral Swab', 'Oropharyngeal swab',
    'Para-Pak', 'PCR', 'Pink Top tube', 'Plain Red Top Tube', 'Plasma',
    'Royal Blue Top Tube', 'Serum', 'Serum Separator tube', 'SPUTUM', 'Stool,Fresh',
    'Swab', 'Synovial fluid', 'Urea breath bags', 'Urethral swab', 'Urine Cup',
    'Urine in Gray top tube', 'Urine Molecular', 'Urine Toxicology', 'Vaginal swab',
    'Whole Blood', 'Wound', 'Yellow Top Tube - ACD A or B',
  ];
  const [specimenSearches, setSpecimenSearches] = useState({});
  const [showSpecimenDropdown, setShowSpecimenDropdown] = useState(null);
  const specimenDropdownRef = useRef(null);

  // Patient search
  const [existingPatients, setExistingPatients] = useState([]);
  const [patientNameSearch, setPatientNameSearch] = useState('');
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const patientNameRef = useRef(null);

  // Step 2: Patients
  const [patients, setPatients] = useState([]);
  const [currentPatient, setCurrentPatient] = useState({
    name: '',
    dob: '',
    gender: '',
    address: '',
    specimens: [{ quantity: 1, type: '' }],
    diagnosis: [''],
    tests: [],
    insuranceCompany: '',
    policyNumber: '',
    scriptFiles: [],
    insuranceFiles: [],
    patientIdFiles: [],
  });

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0, phase: '' });

  // ── Data Fetching ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchPhleb = async () => {
      try {
        const { data, error } = await supabase
          .from('phlebotomist_profiles')
          .select('*')
          .eq('id', phlebotomistId)
          .single();
        if (!error && data) setPhleb(data);
      } catch (e) {
        console.error('Error fetching phlebotomist:', e);
      } finally {
        setPhlebLoading(false);
      }
    };
    if (phlebotomistId) fetchPhleb();
  }, [phlebotomistId]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const { data, error } = await supabase
          .from('doctors')
          .select('id, full_name, email, phone, clinic_name, address')
          .order('full_name');
        if (!error && data) setDoctorOptions(data);
      } catch (e) {
        console.error('Error loading doctors:', e);
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchTests = async () => {
      try {
        const fetchAllRows = async (table, selectQuery) => {
          const PAGE = 1000;
          let allData = [];
          let from = 0;
          while (true) {
            const { data, error } = await supabase
              .from(table).select(selectQuery).order('name').range(from, from + PAGE - 1);
            if (error) throw error;
            if (!data || data.length === 0) break;
            allData = allData.concat(data);
            if (data.length < PAGE) break;
            from += PAGE;
          }
          return allData;
        };
        const selectCols = 'id, code, name, category, tube_top_color, preferred_specimen, alternative_name';
        const [testsData, panelsData] = await Promise.all([
          fetchAllRows('tests', selectCols),
          fetchAllRows('panels', selectCols),
        ]);
        const tests = testsData.map((t) => ({ ...t, type: 'test' }));
        const panels = panelsData.map((p) => ({ ...p, type: 'panel' }));
        setAllTests([...panels, ...tests]);
      } catch (e) {
        console.error('Error loading tests:', e);
      }
    };
    fetchTests();
  }, []);

  useEffect(() => {
    const fetchInsurances = async () => {
      try {
        const { data, error } = await supabase
          .from('insurances')
          .select('id, name')
          .order('name');
        if (!error && data) setAllInsurances(data);
      } catch (e) {
        console.error('Error loading insurances:', e);
      }
    };
    fetchInsurances();
  }, []);

  // Fetch existing patients for autocomplete
  useEffect(() => {
    const fetchExistingPatients = async () => {
      try {
        const { data } = await supabase
          .from('submissions')
          .select('patient_name, patient_dob, patient_gender, patient_address, patient_email, insurance_company, insurance_policy_number, patient_id_image')
          .not('patient_name', 'is', null)
          .order('submitted_at', { ascending: false })
          .limit(500);
        if (data) {
          // Deduplicate by name
          const seen = new Set();
          const unique = data.filter(p => {
            const key = (p.patient_name || '').toLowerCase().trim();
            if (seen.has(key) || !key) return false;
            seen.add(key);
            return true;
          });
          setExistingPatients(unique);
        }
      } catch (e) { console.error('Error loading patients:', e); }
    };
    fetchExistingPatients();
  }, []);

  const fetchLabs = useCallback(async (searchTerm) => {
    try {
      const q = (searchTerm || '').trim();
      let query = supabase
        .from('labs')
        .select('id, name, contact_email, city, state')
        .order('name')
        .limit(30);
      if (q) {
        query = query.or(`name.ilike.%${q}%,city.ilike.%${q}%,state.ilike.%${q}%`);
      }
      const { data, error } = await query;
      if (!error && data) setLabs(data);
    } catch (e) {
      console.error('Error fetching labs:', e);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchLabs(labSearch), 400);
    return () => clearTimeout(timer);
  }, [labSearch, fetchLabs]);

  // Click-outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setShowDoctorDropdown(false);
      }
      if (labDropdownRef.current && !labDropdownRef.current.contains(event.target)) {
        setShowLabDropdown(false);
      }
      if (testDropdownRef.current && !testDropdownRef.current.contains(event.target)) {
        setShowTestDropdown(false);
      }
      if (specimenDropdownRef.current && !specimenDropdownRef.current.contains(event.target)) {
        setShowSpecimenDropdown(null);
      }
      if (insuranceDropdownRef.current && !insuranceDropdownRef.current.contains(event.target)) {
        setShowInsuranceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Doctor filtering ───────────────────────────────────────────────────────────

  const filteredDoctors = doctorOptions.filter((d) => {
    const q = doctorSearch.toLowerCase();
    if (!q) return true;
    return (
      (d.full_name || '').toLowerCase().includes(q) ||
      (d.clinic_name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q)
    );
  }).slice(0, 20);

  const matchesAcronym = (name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  };

  const filteredTests = allTests.filter((t) => {
    const q = testSearch.toLowerCase();
    if (!q) return true;
    return (
      (t.name || '').toLowerCase().includes(q) ||
      (t.alternative_name || '').toLowerCase().includes(q) ||
      matchesAcronym(t.name, q)
    );
  }).slice(0, 30);

  const toggleTest = (test) => {
    setSelectedTests((prev) => {
      const exists = prev.find((t) => t.id === test.id);
      if (exists) return prev; // Already selected, do nothing
      return [...prev, test];
    });
    setTestSearch('');
    setShowTestDropdown(false);
  };

  const removeTest = (testId) => {
    setSelectedTests((prev) => prev.filter((t) => t.id !== testId));
  };

  // Click-outside handler for patient name dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (patientNameRef.current && !patientNameRef.current.contains(e.target)) {
        setShowPatientNameDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Patient helpers ────────────────────────────────────────────────────────────

  const addPatient = () => {
    if (!currentPatient.name.trim()) {
      toast({ title: 'Patient name is required', status: 'warning', duration: 2000 });
      return;
    }
    if (!currentPatient.gender) {
      toast({ title: 'Patient gender is required', status: 'warning', duration: 2000 });
      return;
    }
    // Warn about empty specimen rows
    const emptySpecimens = currentPatient.specimens.filter(s => !s.type).length;
    const totalSpecimens = currentPatient.specimens.length;
    if (emptySpecimens > 0 && emptySpecimens < totalSpecimens) {
      toast({ title: `${emptySpecimens} empty specimen row(s) will be skipped`, status: 'info', duration: 2500 });
    }
    setPatients((prev) => [...prev, { ...currentPatient, id: Date.now().toString() }]);
    setCurrentPatient({
      name: '',
      dob: '',
      gender: '',
      address: '',
      specimens: [{ quantity: 1, type: '' }],
      diagnosis: [''],
      tests: [],
      insuranceCompany: '',
      policyNumber: '',
      scriptFiles: [],
      insuranceFiles: [],
      patientIdFiles: [],
    });
    setPatientNameSearch('');
    setShowPatientNameDropdown(false);
    setSpecimenSearches({});
    setShowSpecimenDropdown(null);
    setTestSearch('');
    setShowTestDropdown(false);
    setInsuranceSearch('');
    setShowInsuranceDropdown(false);
    toast({ title: 'Patient added', status: 'success', duration: 1500 });
  };

  const removePatient = (id) => {
    setPatients((prev) => prev.filter((p) => p.id !== id));
  };

  const updateCurrentFiles = (type, newFiles) => {
    setCurrentPatient((prev) => ({ ...prev, [type]: [...prev[type], ...newFiles] }));
  };

  const removeCurrentFile = (type, index) => {
    setCurrentPatient((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────────

  const submittingRef = useRef(false);

  const handleSubmit = async () => {
    // Prevent double-submission via ref (synchronous guard)
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const total = patients.length;

    try {
      // Phase 1: Upload all files
      const uploadedPatients = [];
      for (let i = 0; i < patients.length; i++) {
        const p = patients[i];
        setSubmitProgress({ current: i + 1, total, phase: `Uploading files for ${p.name}...` });

        const scriptUrls = [];
        for (const file of p.scriptFiles) {
          const url = await uploadFileToStorage(file, 'script');
          scriptUrls.push(url);
        }

        const insuranceUrls = [];
        for (const file of p.insuranceFiles) {
          const url = await uploadFileToStorage(file, 'insurance');
          insuranceUrls.push(url);
        }

        const patientIdUrls = [];
        for (const file of p.patientIdFiles) {
          const url = await uploadFileToStorage(file, 'patient_id');
          patientIdUrls.push(url);
        }

        uploadedPatients.push({ ...p, scriptUrls, insuranceUrls, patientIdUrls });
      }

      // Phase 2: Generate draw codes
      setSubmitProgress({ current: 0, total: 1, phase: 'Generating draw codes...' });
      const drawCodes = await generateBatchDrawCodes(patients.length);

      // Phase 3: Build submission rows
      const phlebName = phleb?.company_name || phleb?.full_name || '';
      const phlebEmail = phleb?.email || '';
      const now = new Date().toISOString();

      // Check if running in facility context — verify against current login
      const facilityCtx = (() => {
        try {
          const ctx = JSON.parse(sessionStorage.getItem('facilityContext') || 'null');
          if (!ctx) return null;
          // Verify the context matches the currently logged-in facility
          const currentFacility = JSON.parse(localStorage.getItem('facilityUser') || 'null');
          if (!currentFacility || currentFacility.id !== ctx.facility_id) {
            sessionStorage.removeItem('facilityContext');
            return null;
          }
          return ctx;
        } catch { return null; }
      })();

      const rows = uploadedPatients.map((p, i) => ({
        doctor_name: selectedDoctor?.full_name || '',
        doctor_email: selectedDoctor?.email || '',
        doctor_phone: selectedDoctor?.phone || '',
        doctor_address: selectedDoctor?.address || '',
        doctor_fax: selectedDoctor?.fax || '',
        client_name: clientName || facilityCtx?.facility_name || null,
        lab_id: selectedLab?.id || null,
        patient_name: p.name || '',
        patient_dob: p.dob || '',
        patient_gender: p.gender || '',
        patient_address: p.address || '',
        script_image: p.scriptUrls || [],
        insurance_company: p.insuranceCompany || null,
        insurance_policy_number: p.policyNumber || null,
        insurance_card_image: p.insuranceUrls || [],
        patient_id_image: p.patientIdUrls || [],
        phlebotomist_id: phlebotomistId || null,
        phlebotomist_name: phlebName || null,
        phlebotomist_email: phlebEmail || null,
        facility_id: facilityCtx?.facility_id || null,
        status: 'pending',
        submitted_at: now,
        draw_code: drawCodes[i],
        tests_on_the_script: (p.tests || []).map((t) => ({ id: t.id })),
        specimen_type: p.specimens?.filter(s => s.type).length > 0
          ? JSON.stringify(p.specimens.filter(s => s.type))
          : null,
        diagnosis: Array.isArray(p.diagnosis) ? (p.diagnosis.filter(d => d.trim()).join('; ') || null) : (p.diagnosis || null),
        created_by_user: facilityCtx ? `FAC-${facilityCtx.facility_name}` : (phlebName || 'LAB'),
      }));

      // Phase 4: Insert all submissions (retry on draw_code collision)
      setSubmitProgress({ current: 0, total: 1, phase: 'Creating submissions...' });
      let error = null;
      for (let retry = 0; retry < 3; retry++) {
        const result = await supabase.from('submissions').insert(rows);
        error = result.error;
        if (!error) break;
        if (error.message?.includes('draw_code') || error.message?.includes('duplicate')) {
          // Regenerate draw codes and retry
          const newCodes = await generateBatchDrawCodes(rows.length);
          rows.forEach((r, i) => { r.draw_code = newCodes[i]; });
          continue;
        }
        break;
      }
      if (error) {
        // Clean up orphaned files if insert fails
        const allUrls = uploadedPatients.flatMap(p => [
          ...(p.scriptUrls || []),
          ...(p.insuranceUrls || []),
          ...(p.patientIdUrls || []),
        ]);
        for (const url of allUrls) {
          try {
            const path = url.split('/uploads/')[1];
            if (path) await supabase.storage.from('uploads').remove([path]);
          } catch (cleanupErr) { console.error('File cleanup failed:', cleanupErr); }
        }
        throw error;
      }

      toast({
        title: `${patients.length} submission${patients.length > 1 ? 's' : ''} created`,
        description: `Draw codes ${drawCodes[0]}${drawCodes.length > 1 ? ` to ${drawCodes[drawCodes.length - 1]}` : ''}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      if (facilityCtx?.facility_id) {
        navigate(`/facility/${facilityCtx.facility_id}`);
      } else {
        navigate(`/phlebotomist/${phlebotomistId}`);
      }
    } catch (e) {
      console.error('Batch submit error:', e);
      toast({
        title: 'Submit failed',
        description: e.message || 'An error occurred. Please try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────────

  const isStepValid = (s) => {
    switch (s) {
      case 0:
        return selectedDoctor !== null;
      case 1:
        return patients.length > 0;
      default:
        return true;
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────────

  if (phlebLoading) {
    return (
      <Container maxW="4xl" py={8}>
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────────

  return (
    <Box minH="100vh" bg="gray.50">
      {/* ── Hero Header — Gradient ──────────────────────── */}
      <Box bgGradient="linear(135deg, teal.500, blue.600)" position="relative" overflow="hidden">
        <Box position="absolute" top={-20} right={-10} w="280px" h="280px" bg="whiteAlpha.100" borderRadius="full" />
        <Box position="absolute" bottom={-32} left="30%" w="320px" h="320px" bg="whiteAlpha.50" borderRadius="full" />

        <Container maxW="container.xl" py={5} position="relative">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={4}>
              <Box bg="white" p={2} borderRadius="xl" cursor="pointer" onClick={() => navigate(`/phlebotomist/${phlebotomistId}`)} boxShadow="md">
                <Image src="/logo.png" alt="AtHomeBloodDraw.com" h="36px" objectFit="contain" />
              </Box>
              {phleb && (
                <Box color="white">
                  <Text fontSize="lg" fontWeight="800" letterSpacing="-0.3px">{phleb.company_name || phleb.full_name || 'Phlebotomist'}</Text>
                  <HStack spacing={3} fontSize="xs" opacity={0.9} flexWrap="wrap">
                    {phleb.company_address && <Text>{phleb.company_address}</Text>}
                    {phleb.email && <Text>•  {phleb.email}</Text>}
                    {phleb.phone && <Text>•  {phleb.phone}</Text>}
                  </HStack>
                </Box>
              )}
            </HStack>
            <Button
              size="sm"
              bg="whiteAlpha.300"
              color="white"
              _hover={{ bg: 'whiteAlpha.400' }}
              backdropFilter="blur(10px)"
              onClick={() => navigate(`/phlebotomist/${phlebotomistId}`)}
              borderRadius="full"
              px={5}
              fontWeight="600"
            >
              ← Back to Dashboard
            </Button>
          </Flex>
        </Container>
      </Box>

    <Container maxW="4xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Title Card */}
        <Card bg="white" boxShadow="md" borderRadius="2xl" overflow="hidden">
          <Box bgGradient="linear(to-r, blue.500, teal.500)" h="3px" />
          <CardBody py={5} px={6}>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <Box>
                <HStack spacing={2} mb={1}>
                  <Box bg="blue.50" color="blue.600" p={1.5} borderRadius="md">
                    <Icon as={FaClipboardList} boxSize={4} />
                  </Box>
                  <Text fontSize="2xl" fontWeight="800" color="gray.800" letterSpacing="-0.5px">
                    Bulk Upload
                  </Text>
                </HStack>
                <Text color="gray.600" fontSize="sm" pl={9}>
                  Submit multiple patients under the same doctor and lab in one go.
                </Text>
              </Box>
            </Flex>
          </CardBody>
        </Card>

        {/* Step indicator — modern stepper */}
        <Card bg="white" boxShadow="sm" borderRadius="2xl" overflow="hidden">
          <CardBody py={5}>
            <HStack spacing={0} justify="center">
              {STEPS.map((s, i) => (
                <HStack key={i} spacing={2}>
                  {i > 0 && (
                    <Box
                      w={16}
                      h="3px"
                      bgGradient={i <= step ? 'linear(to-r, teal.400, blue.500)' : 'linear(to-r, gray.200, gray.200)'}
                      borderRadius="full"
                      transition="all 0.3s"
                    />
                  )}
                  <VStack spacing={2}>
                    <Flex
                      w={12}
                      h={12}
                      borderRadius="full"
                      bgGradient={i <= step ? 'linear(135deg, teal.400, blue.500)' : 'linear(135deg, gray.100, gray.200)'}
                      align="center"
                      justify="center"
                      transition="all 0.3s"
                      boxShadow={i === step ? 'lg' : 'sm'}
                      transform={i === step ? 'scale(1.1)' : 'scale(1)'}
                      border="3px solid"
                      borderColor={i === step ? 'white' : 'transparent'}
                    >
                      <Icon as={s.icon} color={i <= step ? 'white' : 'gray.400'} boxSize={5} />
                    </Flex>
                    <Text
                      fontSize="xs"
                      color={i <= step ? 'gray.800' : 'gray.400'}
                      fontWeight={i === step ? '800' : '500'}
                      textTransform="uppercase"
                      letterSpacing="0.5px"
                    >
                      {s.label}
                    </Text>
                  </VStack>
                </HStack>
              ))}
            </HStack>
          </CardBody>
        </Card>

        {/* Step content */}
        {step === 0 && (
          <Card bg="white" boxShadow="md" borderRadius="2xl" overflow="hidden">
            <CardBody p={6}>
              <VStack spacing={5} align="stretch">
            {/* Doctor dropdown */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="700" color="gray.700">Doctor</FormLabel>
              <Box ref={doctorDropdownRef} position="relative">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by name, clinic, or email..."
                    value={doctorSearch}
                    onChange={(e) => {
                      setDoctorSearch(e.target.value);
                      setShowDoctorDropdown(true);
                      if (selectedDoctor) setSelectedDoctor(null);
                    }}
                    onFocus={() => setShowDoctorDropdown(true)}
                  />
                </InputGroup>
                {showDoctorDropdown && (
                  <Box
                    position="absolute"
                    zIndex={20}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    maxH="250px"
                    overflowY="auto"
                    w="100%"
                    mt={1}
                  >
                    {filteredDoctors.length === 0 ? (
                      <Text p={3} fontSize="sm" color="gray.500">
                        No doctors found
                      </Text>
                    ) : (
                      filteredDoctors.map((d) => (
                        <Box
                          key={d.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => {
                            setSelectedDoctor(d);
                            setDoctorSearch(d.full_name);
                            setClientName(d.clinic_name || '');
                            setShowDoctorDropdown(false);
                          }}
                        >
                          <Text fontWeight="medium" fontSize="sm">
                            {d.full_name}
                          </Text>
                          {d.clinic_name && (
                            <Text fontSize="xs" color="gray.500">
                              {d.clinic_name}
                            </Text>
                          )}
                          {d.email && (
                            <Text fontSize="xs" color="gray.400">
                              {d.email}
                            </Text>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </Box>
              {selectedDoctor && (
                <Badge mt={2} colorScheme="green" px={2} py={1} borderRadius="md">
                  <Icon as={FaCheckCircle} mr={1} /> Selected: {selectedDoctor.full_name}
                </Badge>
              )}
            </FormControl>

            {/* Client Name */}
            <FormControl>
              <FormLabel>Client Name</FormLabel>
              <Input
                placeholder="Facility or client name (e.g. senior living, practice)"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            </FormControl>

            {/* Lab dropdown */}
            <FormControl>
              <FormLabel>Lab</FormLabel>
              <Box ref={labDropdownRef} position="relative">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search lab by name, city, or state..."
                    value={labSearch}
                    onChange={(e) => {
                      setLabSearch(e.target.value);
                      setShowLabDropdown(true);
                      if (selectedLab) setSelectedLab(null);
                    }}
                    onFocus={() => setShowLabDropdown(true)}
                  />
                </InputGroup>
                {showLabDropdown && (
                  <Box
                    position="absolute"
                    zIndex={20}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    maxH="250px"
                    overflowY="auto"
                    w="100%"
                    mt={1}
                  >
                    {labs.length === 0 ? (
                      <Text p={3} fontSize="sm" color="gray.500">
                        No labs found
                      </Text>
                    ) : (
                      labs.map((l) => (
                        <Box
                          key={l.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          onClick={() => {
                            setSelectedLab(l);
                            setLabSearch(l.name);
                            setShowLabDropdown(false);
                          }}
                        >
                          <Text fontWeight="medium" fontSize="sm">
                            {l.name}
                          </Text>
                          {(l.city || l.state) && (
                            <Text fontSize="xs" color="gray.500">
                              {[l.city, l.state].filter(Boolean).join(', ')}
                            </Text>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                )}
              </Box>
              {selectedLab && (
                <Badge mt={2} colorScheme="green" px={2} py={1} borderRadius="md">
                  <Icon as={FaCheckCircle} mr={1} /> Selected: {selectedLab.name}
                  {selectedLab.city && ` \u2014 ${selectedLab.city}, ${selectedLab.state || ''}`}
                </Badge>
              )}
            </FormControl>

            {/* Tests/Panels moved to per-patient in Step 2 */}
              </VStack>
            </CardBody>
          </Card>
        )}

        {step === 1 && (
          <VStack spacing={5} align="stretch">
            {/* Patient form card */}
            <Card>
              <CardHeader py={3}>
                <Text fontWeight="semibold">Add a Patient</Text>
              </CardHeader>
              <CardBody pt={0}>
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired position="relative" ref={patientNameRef}>
                      <FormLabel fontSize="sm">Patient Name</FormLabel>
                      <Input
                        placeholder="Type or search existing patient..."
                        value={currentPatient.name}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCurrentPatient((p) => ({ ...p, name: val }));
                          setPatientNameSearch(val);
                          setShowPatientNameDropdown(val.length >= 2);
                        }}
                        onFocus={() => { if (currentPatient.name.length >= 2) setShowPatientNameDropdown(true); }}
                        onBlur={() => setTimeout(() => setShowPatientNameDropdown(false), 200)}
                      />
                      {showPatientNameDropdown && (() => {
                        const q = patientNameSearch.toLowerCase();
                        const matches = existingPatients.filter(p => (p.patient_name || '').toLowerCase().includes(q)).slice(0, 10);
                        if (matches.length === 0) return null;
                        return (
                          <Box position="absolute" zIndex={20} bg="white" w="full" border="1px" borderColor="gray.200" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" mt={1}>
                            {matches.map((p, i) => (
                              <Box key={i} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }}
                                onClick={() => {
                                  setCurrentPatient(prev => ({
                                    ...prev,
                                    name: p.patient_name || '',
                                    dob: p.patient_dob || '',
                                    gender: p.patient_gender || '',
                                    address: p.patient_address || '',
                                    insuranceCompany: p.insurance_company || '',
                                    policyNumber: p.insurance_policy_number || '',
                                  }));
                                  // Auto-select insurance in dropdown if it exists
                                  if (p.insurance_company) {
                                    setInsuranceSearch(p.insurance_company);
                                  }
                                  setShowPatientNameDropdown(false);
                                }}>
                                <Text fontSize="sm" fontWeight="500">{p.patient_name}</Text>
                                <HStack spacing={2} flexWrap="wrap">
                                  {p.patient_dob && <Text fontSize="xs" color="gray.500">DOB: {p.patient_dob}</Text>}
                                  {p.patient_gender && <Text fontSize="xs" color="gray.500">{p.patient_gender}</Text>}
                                  {p.insurance_company && <Text fontSize="xs" color="blue.500">{p.insurance_company}</Text>}
                                </HStack>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Date of Birth</FormLabel>
                      <Input
                        type="date"
                        value={currentPatient.dob}
                        onChange={(e) =>
                          setCurrentPatient((p) => ({ ...p, dob: e.target.value }))
                        }
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Gender</FormLabel>
                      <Select
                        placeholder="Select gender"
                        value={currentPatient.gender}
                        onChange={(e) =>
                          setCurrentPatient((p) => ({ ...p, gender: e.target.value }))
                        }
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Address</FormLabel>
                      <Input
                        placeholder="Patient address"
                        value={currentPatient.address}
                        onChange={(e) =>
                          setCurrentPatient((p) => ({ ...p, address: e.target.value }))
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  {/* Insurance */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Insurance Company</FormLabel>
                      <Box ref={insuranceDropdownRef} position="relative">
                        <Input
                          placeholder="Search insurance..."
                          value={insuranceSearch || currentPatient.insuranceCompany}
                          onChange={(e) => {
                            setInsuranceSearch(e.target.value);
                            setShowInsuranceDropdown(true);
                            if (!e.target.value) {
                              setCurrentPatient(p => ({ ...p, insuranceCompany: '' }));
                            }
                          }}
                          onFocus={() => setShowInsuranceDropdown(true)}
                          size="sm"
                        />
                        {showInsuranceDropdown && (
                          <Box
                            position="absolute" zIndex={20} bg="white" border="1px solid"
                            borderColor="gray.200" borderRadius="md" boxShadow="lg"
                            maxH="200px" overflowY="auto" w="100%" mt={1}
                          >
                            {allInsurances
                              .filter(ins => {
                                const q = (insuranceSearch || '').toLowerCase();
                                return !q || ins.name.toLowerCase().includes(q);
                              })
                              .slice(0, 20)
                              .map(ins => (
                                <Box
                                  key={ins.id} px={3} py={2} cursor="pointer"
                                  _hover={{ bg: 'blue.50' }} fontSize="sm"
                                  onClick={() => {
                                    setCurrentPatient(p => ({ ...p, insuranceCompany: ins.name }));
                                    setInsuranceSearch('');
                                    setShowInsuranceDropdown(false);
                                  }}
                                >
                                  {ins.name}
                                </Box>
                              ))}
                            {allInsurances.filter(ins => {
                              const q = (insuranceSearch || '').toLowerCase();
                              return !q || ins.name.toLowerCase().includes(q);
                            }).length === 0 && (
                              <Text p={3} fontSize="sm" color="gray.500">No insurances found</Text>
                            )}
                          </Box>
                        )}
                      </Box>
                      {currentPatient.insuranceCompany && (
                        <Badge mt={1} colorScheme="green" fontSize="xs">
                          {currentPatient.insuranceCompany}
                        </Badge>
                      )}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Policy Number</FormLabel>
                      <Input
                        placeholder="Policy number"
                        value={currentPatient.policyNumber}
                        onChange={(e) => setCurrentPatient(p => ({ ...p, policyNumber: e.target.value }))}
                        size="sm"
                      />
                    </FormControl>
                  </SimpleGrid>

                  {/* Specimen rows */}
                  <FormControl>
                    <FormLabel fontSize="sm">Specimen</FormLabel>
                    <Box ref={specimenDropdownRef}>
                      {currentPatient.specimens.map((row, idx) => (
                        <HStack key={idx} spacing={2} mb={2}>
                          <Select
                            size="sm" w="80px" value={row.quantity}
                            onChange={(e) => {
                              const updated = [...currentPatient.specimens];
                              updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) };
                              setCurrentPatient(p => ({ ...p, specimens: updated }));
                            }}
                          >
                            {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                          </Select>
                          <Box flex={1} position="relative">
                            <Input
                              size="sm" placeholder="Search specimen type..."
                              value={showSpecimenDropdown === idx ? (specimenSearches[idx] ?? row.type) : row.type}
                              onChange={(e) => {
                                setSpecimenSearches({ ...specimenSearches, [idx]: e.target.value });
                                setShowSpecimenDropdown(idx);
                              }}
                              onFocus={() => {
                                setSpecimenSearches({ ...specimenSearches, [idx]: row.type });
                                setShowSpecimenDropdown(idx);
                              }}
                            />
                            {showSpecimenDropdown === idx && (() => {
                              const searchVal = specimenSearches[idx] || '';
                              const filtered = searchVal
                                ? specimenTypes.filter(s => s.toLowerCase().includes(searchVal.toLowerCase()))
                                : specimenTypes;
                              return filtered.length > 0 ? (
                                <Box
                                  position="absolute" zIndex={20} w="100%" maxH="200px" overflowY="auto"
                                  bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" mt={1} boxShadow="md"
                                >
                                  {filtered.map((st) => (
                                    <Box
                                      key={st} p={2} cursor="pointer" fontSize="sm"
                                      _hover={{ bg: 'blue.50' }}
                                      bg={row.type === st ? 'blue.100' : undefined}
                                      onClick={() => {
                                        const updated = [...currentPatient.specimens];
                                        updated[idx] = { ...updated[idx], type: st };
                                        setCurrentPatient(p => ({ ...p, specimens: updated }));
                                        setShowSpecimenDropdown(null);
                                        setSpecimenSearches({ ...specimenSearches, [idx]: '' });
                                      }}
                                    >
                                      {st}
                                    </Box>
                                  ))}
                                </Box>
                              ) : null;
                            })()}
                          </Box>
                          {currentPatient.specimens.length > 1 && (
                            <IconButton
                              icon={<CloseIcon />} size="xs" variant="ghost" colorScheme="red"
                              aria-label="Remove specimen row"
                              onClick={() => {
                                const updated = currentPatient.specimens.filter((_, i) => i !== idx);
                                setCurrentPatient(p => ({ ...p, specimens: updated }));
                              }}
                            />
                          )}
                        </HStack>
                      ))}
                      <Button
                        size="xs" variant="ghost" colorScheme="blue" leftIcon={<AddIcon />}
                        onClick={() => setCurrentPatient(p => ({ ...p, specimens: [...p.specimens, { quantity: 1, type: '' }] }))}
                      >
                        Add Specimen Row
                      </Button>
                    </Box>
                  </FormControl>

                  {/* Diagnosis */}
                  <FormControl>
                    <FormLabel fontSize="sm">Diagnosis</FormLabel>
                    {(Array.isArray(currentPatient.diagnosis) ? currentPatient.diagnosis : [currentPatient.diagnosis || '']).map((diag, idx) => (
                      <HStack key={idx} mb={2}>
                        <Input
                          placeholder="Enter diagnosis or ICD code..."
                          value={diag}
                          onChange={(e) => {
                            const updated = [...(Array.isArray(currentPatient.diagnosis) ? currentPatient.diagnosis : [currentPatient.diagnosis || ''])];
                            updated[idx] = e.target.value;
                            setCurrentPatient(p => ({ ...p, diagnosis: updated }));
                          }}
                          size="sm"
                        />
                        {(Array.isArray(currentPatient.diagnosis) ? currentPatient.diagnosis : [currentPatient.diagnosis || '']).length > 1 && (
                          <IconButton
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            aria-label="Remove diagnosis row"
                            onClick={() => {
                              const updated = (Array.isArray(currentPatient.diagnosis) ? currentPatient.diagnosis : [currentPatient.diagnosis || '']).filter((_, i) => i !== idx);
                              setCurrentPatient(p => ({ ...p, diagnosis: updated }));
                            }}
                          />
                        )}
                      </HStack>
                    ))}
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      leftIcon={<AddIcon />}
                      onClick={() => setCurrentPatient(p => ({ ...p, diagnosis: [...(Array.isArray(p.diagnosis) ? p.diagnosis : [p.diagnosis || '']), ''] }))}
                    >
                      Add Diagnosis Row
                    </Button>
                  </FormControl>

                  {/* Tests / Panels (per patient) */}
                  <FormControl>
                    <FormLabel fontSize="sm">Tests / Panels</FormLabel>
                    <Box ref={testDropdownRef} position="relative">
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={FaVial} color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search by test name, code, or category..."
                          value={testSearch}
                          onChange={(e) => {
                            setTestSearch(e.target.value);
                            setShowTestDropdown(true);
                          }}
                          onFocus={() => setShowTestDropdown(true)}
                        />
                      </InputGroup>
                      {showTestDropdown && (
                        <Box
                          position="absolute"
                          zIndex={20}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          boxShadow="lg"
                          maxH="250px"
                          overflowY="auto"
                          w="100%"
                          mt={1}
                        >
                          {filteredTests.length === 0 ? (
                            <Text p={3} fontSize="sm" color="gray.500">
                              No tests found
                            </Text>
                          ) : (
                            filteredTests
                              .filter((t) => !(currentPatient.tests || []).some((s) => s.id === t.id))
                              .map((t) => (
                                <Box
                                  key={t.id}
                                  px={3}
                                  py={2}
                                  cursor="pointer"
                                  bg="white"
                                  _hover={{ bg: 'gray.50' }}
                                  onClick={() => {
                                    setCurrentPatient(p => ({ ...p, tests: [...(p.tests || []), t] }));
                                    setTestSearch('');
                                    setShowTestDropdown(false);
                                  }}
                                >
                                  <HStack spacing={2}>
                                    <Text fontWeight="medium" fontSize="sm">
                                      {t.name}
                                    </Text>
                                    <Badge size="sm" colorScheme={t.type === 'panel' ? 'purple' : 'gray'}>
                                      {t.type === 'panel' ? 'Panel' : 'Test'}
                                    </Badge>
                                  </HStack>
                                  <HStack spacing={2} mt={0.5}>
                                    {t.code && (
                                      <Text fontSize="xs" color="gray.500">
                                        Code: {t.code}
                                      </Text>
                                    )}
                                    {t.category && (
                                      <Text fontSize="xs" color="gray.400">
                                        {t.category}
                                      </Text>
                                    )}
                                  </HStack>
                                </Box>
                              ))
                          )}
                        </Box>
                      )}
                    </Box>
                    {(currentPatient.tests || []).length > 0 && (
                      <Flex mt={2} gap={2} flexWrap="wrap">
                        {currentPatient.tests.map((t) => (
                          <Badge
                            key={t.id}
                            colorScheme={t.type === 'panel' ? 'purple' : 'blue'}
                            px={2}
                            py={1}
                            borderRadius="md"
                            display="flex"
                            alignItems="center"
                          >
                            {t.name}
                            <IconButton
                              icon={<FaTimes />}
                              size="xs"
                              variant="ghost"
                              ml={1}
                              minW="auto"
                              h="auto"
                              onClick={() => setCurrentPatient(p => ({ ...p, tests: (p.tests || []).filter(pt => pt.id !== t.id) }))}
                              aria-label="Remove test"
                            />
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </FormControl>

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FileDropZone
                      label="Script"
                      icon={FaStethoscope}
                      color="blue"
                      files={currentPatient.scriptFiles}
                      onAdd={(files) => updateCurrentFiles('scriptFiles', files)}
                      onRemove={(i) => removeCurrentFile('scriptFiles', i)}
                    />
                    <FileDropZone
                      label="Insurance Card"
                      icon={FaIdCard}
                      color="green"
                      files={currentPatient.insuranceFiles}
                      onAdd={(files) => updateCurrentFiles('insuranceFiles', files)}
                      onRemove={(i) => removeCurrentFile('insuranceFiles', i)}
                    />
                    <FileDropZone
                      label="Patient ID"
                      icon={FaAddressCard}
                      color="purple"
                      files={currentPatient.patientIdFiles}
                      onAdd={(files) => updateCurrentFiles('patientIdFiles', files)}
                      onRemove={(i) => removeCurrentFile('patientIdFiles', i)}
                    />
                  </SimpleGrid>

                </VStack>
              </CardBody>
              <Box
                borderTop="2px dashed"
                borderColor="green.300"
                bg="green.50"
                px={6}
                py={3}
              >
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600">
                    Fill in the patient details above, then add to the list
                  </Text>
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="green"
                    onClick={addPatient}
                    size="md"
                    fontWeight="semibold"
                    px={6}
                  >
                    Add Patient to List
                  </Button>
                </Flex>
              </Box>
            </Card>

            {/* Patient list */}
            {patients.length > 0 && (
              <Card>
                <CardHeader py={3}>
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">
                      Patients Added ({patients.length})
                    </Text>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack spacing={2} align="stretch">
                    {patients.map((p) => (
                      <Flex
                        key={p.id}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        justify="space-between"
                        align="center"
                      >
                        <HStack spacing={4} flex={1}>
                          <Box>
                            <Text fontWeight="medium" fontSize="sm">
                              {p.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {p.gender}
                              {p.dob && ` | DOB: ${p.dob}`}
                            </Text>
                          </Box>
                          <HStack spacing={2}>
                            {(p.tests || []).length > 0 && (
                              <Badge colorScheme="teal" fontSize="xs">
                                {p.tests.length} test{p.tests.length > 1 ? 's' : ''}
                              </Badge>
                            )}
                            {p.scriptFiles.length > 0 && (
                              <Badge colorScheme="blue" fontSize="xs">
                                {p.scriptFiles.length} script{p.scriptFiles.length > 1 ? 's' : ''}
                              </Badge>
                            )}
                            {p.insuranceFiles.length > 0 && (
                              <Badge colorScheme="green" fontSize="xs">
                                {p.insuranceFiles.length} insurance
                              </Badge>
                            )}
                            {p.patientIdFiles.length > 0 && (
                              <Badge colorScheme="purple" fontSize="xs">
                                {p.patientIdFiles.length} ID
                              </Badge>
                            )}
                          </HStack>
                        </HStack>
                        <IconButton
                          icon={<FaTrash />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          aria-label="Remove patient"
                          onClick={() => removePatient(p.id)}
                        />
                      </Flex>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            )}

            {patients.length === 0 && (
              <Text textAlign="center" color="gray.400" fontSize="sm" py={4}>
                No patients added yet. Fill in the form above and click "Add Patient".
              </Text>
            )}
          </VStack>
        )}

        {step === 2 && (
          <VStack spacing={5} align="stretch">
            {/* Batch info summary */}
            <Card>
              <CardHeader py={3}>
                <Text fontWeight="semibold">Batch Info</Text>
              </CardHeader>
              <CardBody pt={0}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      Doctor
                    </Text>
                    <Text fontWeight="medium">{selectedDoctor?.full_name}</Text>
                    {selectedDoctor?.email && (
                      <Text fontSize="xs" color="gray.500">
                        {selectedDoctor.email}
                      </Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      Client
                    </Text>
                    <Text fontWeight="medium">{clientName || '\u2014'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase">
                      Lab
                    </Text>
                    <Text fontWeight="medium">{selectedLab?.name || '\u2014'}</Text>
                    {selectedLab?.city && (
                      <Text fontSize="xs" color="gray.500">
                        {selectedLab.city}, {selectedLab.state || ''}
                      </Text>
                    )}
                  </Box>
                </SimpleGrid>
                {/* Tests/Panels are now per-patient, shown in the table below */}
              </CardBody>
            </Card>

            {/* Patients table */}
            <Card>
              <CardHeader py={3}>
                <Text fontWeight="semibold">
                  Patients ({patients.length})
                </Text>
              </CardHeader>
              <CardBody pt={0}>
                <Box overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>#</Th>
                        <Th>Patient Name</Th>
                        <Th>DOB</Th>
                        <Th>Gender</Th>
                        <Th>Tests</Th>
                        <Th>Specimen</Th>
                        <Th>Diagnosis</Th>
                        <Th>Scripts</Th>
                        <Th>Insurance</Th>
                        <Th>IDs</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {patients.map((p, i) => (
                        <Tr key={p.id}>
                          <Td>{i + 1}</Td>
                          <Td fontWeight="medium">{p.name}</Td>
                          <Td>{p.dob || '\u2014'}</Td>
                          <Td>{p.gender}</Td>
                          <Td fontSize="xs">
                            {(p.tests || []).length > 0
                              ? p.tests.map((t, i) => (
                                  <Badge key={i} size="sm" colorScheme={t.type === 'panel' ? 'purple' : 'blue'} mr={1} mb={1}>{t.name}</Badge>
                                ))
                              : '\u2014'}
                          </Td>
                          <Td fontSize="xs">
                            {p.specimens?.filter(s => s.type).map((s, i) => (
                              <Text key={i}>{s.quantity}x {s.type}</Text>
                            ))}
                            {(!p.specimens || !p.specimens.some(s => s.type)) && '\u2014'}
                          </Td>
                          <Td fontSize="xs">
                            {Array.isArray(p.diagnosis)
                              ? (p.diagnosis.filter(d => d.trim()).length > 0
                                ? p.diagnosis.filter(d => d.trim()).map((d, i) => <Text key={i}>{d}</Text>)
                                : '\u2014')
                              : (p.diagnosis || '\u2014')}
                          </Td>
                          <Td>
                            <Badge colorScheme="blue">{p.scriptFiles.length}</Badge>
                          </Td>
                          <Td fontSize="xs">
                            {p.insuranceCompany && <Text>{p.insuranceCompany}</Text>}
                            {p.policyNumber && <Text color="gray.500">#{p.policyNumber}</Text>}
                            {p.insuranceFiles.length > 0 && <Badge colorScheme="green" mt={1}>{p.insuranceFiles.length} file{p.insuranceFiles.length > 1 ? 's' : ''}</Badge>}
                            {!p.insuranceCompany && !p.policyNumber && p.insuranceFiles.length === 0 && '\u2014'}
                          </Td>
                          <Td>
                            <Badge colorScheme="purple">{p.patientIdFiles.length}</Badge>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>

            {/* Submit progress */}
            {submitting && (
              <Card>
                <CardBody>
                  <HStack mb={2}>
                    <Spinner size="sm" />
                    <Text fontSize="sm">{submitProgress.phase}</Text>
                  </HStack>
                  <Progress
                    size="sm"
                    colorScheme="blue"
                    value={
                      submitProgress.total > 0
                        ? (submitProgress.current / submitProgress.total) * 100
                        : 0
                    }
                    isIndeterminate={submitProgress.total === 0}
                    borderRadius="full"
                  />
                  {submitProgress.total > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {submitProgress.current} of {submitProgress.total}
                    </Text>
                  )}
                </CardBody>
              </Card>
            )}
          </VStack>
        )}

        {/* Step navigation */}
        <HStack justify="space-between" pt={2}>
          <Button
            leftIcon={<ChevronLeftIcon />}
            variant="outline"
            onClick={() => setStep((s) => s - 1)}
            isDisabled={step === 0}
          >
            Previous
          </Button>
          {step < 2 ? (
            <Button
              rightIcon={<ChevronRightIcon />}
              colorScheme="blue"
              onClick={() => setStep((s) => s + 1)}
              isDisabled={!isStepValid(step)}
            >
              {step === 1 ? 'Review' : 'Next'}
            </Button>
          ) : (
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Submitting..."
              isDisabled={submitting}
            >
              Submit {patients.length} Patient{patients.length !== 1 ? 's' : ''}
            </Button>
          )}
        </HStack>
      </VStack>
    </Container>
    </Box>
  );
}
